import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Tooltip } from '@mui/material';
import { motion } from 'framer-motion';

// Simple color mapping for status
const statusColor = (status: string) => {
  switch (status) {
    case 'Building': return 'text-momentum-teal';
    case 'Maintaining': return 'text-momentum-emerald';
    case 'Declining': return 'text-momentum-amber';
    default: return 'text-white/70';
  }
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'Building': return <ArrowUpwardIcon className="!text-momentum-teal animate-pulse" />;
    case 'Maintaining': return <ArrowForwardIcon className="!text-momentum-emerald animate-pulse" />;
    case 'Declining': return <ArrowDownwardIcon className="!text-momentum-amber animate-pulse" />;
    default: return null;
  }
};

const getUserIdFromToken = (): string | null => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const decoded: unknown = jwtDecode(token);
    // Type guard for decoded object
    if (typeof decoded === 'object' && decoded !== null && 'user' in decoded) {
      // @ts-expect-error: dynamic structure from jwtDecode
      return decoded.user?.id || null;
    }
    return null;
  } catch {
    return null;
  }
};

const factorExplanations: Record<string, string> = {
  consistency: 'How regularly you check in (higher = more consistent)',
  sentiment: 'Positivity of your check-in text (higher = more positive)',
  progress: 'Your 7-day average progress score',
};

const statusExplanations: Record<string, string> = {
  Building: 'Your momentum is increasing! Keep it up!',
  Maintaining: 'You are maintaining your momentum.',
  Declining: 'Your momentum is declining. Consider taking action.',
};

// Define a type for momentum data
interface MomentumData {
  score: number;
  status: string;
  factors: Record<string, number>;
  calculatedAt?: string;
}

const MomentumDashboard: React.FC = () => {
  const [current, setCurrent] = useState<MomentumData | null>(null);
  const [history, setHistory] = useState<Array<{ score: number; calculatedAt: string; status: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);

  const userId = getUserIdFromToken();

  useEffect(() => {
    if (!userId) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };
        // Fetch last two momentum scores
        const resHistory = await axios.get(`/api/checkins/momentum/${userId}/history?days=2`, config);
        const historyData = resHistory.data as Array<{ score: number; calculatedAt: string; status: string }>;
        setHistory(historyData);
        if (historyData.length > 1) {
          const prev = historyData[historyData.length - 2].status;
          const curr = historyData[historyData.length - 1].status;
          // Only show notification if status changed and not already seen
          const lastSeen = localStorage.getItem('momentum_last_seen_status');
          if (prev !== curr && lastSeen !== curr) {
            let msg = '';
            if (curr === 'Building') msg = 'Momentum is building! Keep up the great work!';
            if (curr === 'Maintaining') msg = 'You are maintaining your momentum.';
            if (curr === 'Declining') msg = 'Momentum is declining. Consider taking action!';
            setNotification(msg);
            localStorage.setItem('momentum_last_seen_status', curr);
          } else {
            setNotification(null);
          }
        } else {
          setNotification(null);
        }
        // Fetch current momentum
        const resCurrent = await axios.get(`/api/checkins/momentum/${userId}`, config);
        setCurrent(resCurrent.data as MomentumData);
      } catch {
        setError('Failed to fetch momentum data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  if (loading) return <div>Loading momentum...</div>;
  if (error) return <div className="text-momentum-amber">{error}</div>;
  if (!current) return <div>No momentum data available.</div>;

  const lastUpdated = current.calculatedAt ? new Date(current.calculatedAt).toLocaleString() : '';

  return (
    <motion.div
      className="w-full max-w-2xl mx-auto p-6 glass shadow-glass rounded-2xl font-inter"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 80, damping: 18 }}
    >
      <h2 className="mb-4 text-2xl font-bold text-momentum-teal font-inter">Momentum Dashboard</h2>
      {notification && (
        <motion.div
          className="bg-momentum-building/30 text-momentum-teal px-4 py-2 rounded-xl mb-4 font-medium flex items-center gap-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <InfoOutlinedIcon className="!text-momentum-teal" />
          {notification}
        </motion.div>
      )}
      <div className="flex items-center gap-4 mb-6">
        <motion.div
          className="flex items-center gap-2"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          {statusIcon(current.status)}
          <span className={`font-semibold text-xl ml-1 ${statusColor(current.status)} animate-pulse`}>{current.status}</span>
          <Tooltip title={statusExplanations[current.status] || ''} placement="right">
            <InfoOutlinedIcon className="!text-white/60 ml-1" />
          </Tooltip>
        </motion.div>
        <motion.span
          className="ml-6 text-lg font-bold text-white/90 px-3 py-1 rounded-xl bg-dark-glass shadow-glass animate-pulse"
          animate={{ scale: [1, 1.12, 1] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
        >
          Score: {current.score.toFixed(2)}
        </motion.span>
      </div>
      <div className="mb-4">
        <strong className="text-momentum-emerald font-inter">Factors:</strong>
        <ul className="mt-1 ml-4">
          {Object.entries(current.factors).map(([key, value]) => (
            <li key={key} className="mb-1 text-white/80 font-inter flex items-center gap-1">
              <span className="capitalize text-momentum-amber">{key}</span>: {(value as number).toFixed(2)}
              <Tooltip title={factorExplanations[key] || ''} placement="right">
                <InfoOutlinedIcon className="!text-white/40 ml-1" />
              </Tooltip>
            </li>
          ))}
        </ul>
      </div>
      <div className="mb-6">
        <strong className="text-momentum-emerald font-inter">Momentum Trend (Last 7 Days):</strong>
        <div className="w-full h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={history.map(h => ({ ...h, date: new Date(h.calculatedAt).toLocaleDateString() }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2226" />
              <XAxis dataKey="date" fontSize={12} stroke="#fff" />
              <YAxis domain={[0, 1]} tickFormatter={v => v.toFixed(1)} fontSize={12} stroke="#fff" />
              <RechartsTooltip formatter={(v: number) => v.toFixed(2)} contentStyle={{ background: '#222', borderRadius: 8, color: '#fff' }} />
              <Line type="monotone" dataKey="score" stroke="#20E3B2" strokeWidth={3} dot={{ r: 4, fill: '#FBBF24' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="text-xs text-white/40 text-right font-inter">
        Last updated: {lastUpdated}
      </div>
    </motion.div>
  );
};

export default MomentumDashboard; 